import { create } from "zustand";
import type { SearchFilter, Uuid } from "@shared/api";
import type { Has } from "../lib/search-tokens";

const HISTORY_KEY = "airel.search-history";
const HISTORY_MAX = 5;

/** Недавние запросы — как «История» в поиске Discord; хранятся у себя. */
export function readHistory(): string[] {
  try {
    const value = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]") as unknown;
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function writeHistory(items: string[]) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(items));
  } catch {
    // Хранилище закрыто — история просто не запомнится.
  }
}

/**
 * Фильтры — как окно «Фильтры» в Discord: от кого, где, что есть, кого зовут,
 * когда, кем написано и закреплено ли. Списки — «любой из выбранных».
 */
export interface Filters {
  authors: Uuid[];
  channels: Uuid[];
  mentions: Uuid[];
  has: Has | null;
  /** Даты — днём (`YYYY-MM-DD`), как в поле выбора даты. */
  after: string | null;
  before: string | null;
  pinned: boolean | null;
  authorType: "user" | "bot" | null;
  /** Только в этой ветке — «Поиск» из меню ветки; имя — для плашки в поле. */
  thread: { id: Uuid; name: string } | null;
}

export const NO_FILTERS: Filters = {
  authors: [],
  channels: [],
  mentions: [],
  has: null,
  after: null,
  before: null,
  pinned: null,
  authorType: null,
  thread: null,
};

/** Сколько фильтров задано — «Фильтры (2)», как в Discord. */
export function filterCount(filters: Filters): number {
  return (
    filters.authors.length +
    filters.channels.length +
    filters.mentions.length +
    (filters.has ? 1 : 0) +
    (filters.after ? 1 : 0) +
    (filters.before ? 1 : 0) +
    (filters.pinned !== null ? 1 : 0) +
    (filters.authorType ? 1 : 0) +
    (filters.thread ? 1 : 0)
  );
}

/** Фильтры — для запроса: даты днём превращаются во время, «до» — включительно. */
export function toRequest(filters: Filters): SearchFilter {
  const day = (value: string, shift = 0) => {
    const at = new Date(`${value}T00:00:00`);
    at.setDate(at.getDate() + shift);
    return at.toISOString();
  };
  return {
    authorIds: filters.authors,
    channelIds: filters.channels,
    mentionIds: filters.mentions,
    has: filters.has,
    after: filters.after ? day(filters.after) : null,
    before: filters.before ? day(filters.before, 1) : null,
    pinned: filters.pinned,
    authorType: filters.authorType,
    threadId: filters.thread?.id ?? null,
  };
}

interface Search extends Filters {
  /** Набранное в поле — без законченных фильтров. */
  text: string;
  /** Запущенный запрос; `null` — поиск не начат, колонки находок нет. */
  query: string | null;
  /** Как в Discord: по умолчанию — весь сервер. */
  scope: "server" | "channel";
  /** Порядок находок: новые, старые или по совпадению. Переживает новый запрос — как в Discord. */
  sort: "newest" | "oldest" | "relevant";
  history: string[];
  /** Открыто окно «Фильтры». */
  editing: boolean;
  setText: (text: string) => void;
  /** Поправить фильтры — частью. */
  patch: (next: Partial<Filters>) => void;
  setScope: (scope: "server" | "channel") => void;
  setSort: (sort: Search["sort"]) => void;
  setEditing: (editing: boolean) => void;
  /** Запустить: запрос — из поля; непустой запоминается в истории. */
  submit: (text?: string) => void;
  clear: () => void;
  forget: () => void;
}

/**
 * Поиск по сообщениям — одно состояние на поле в шапке, окно «Фильтры» и
 * колонку находок, как в Discord: в шапке набирают и выбирают фильтры, справа —
 * что нашлось. Фильтр, тронутый уже после запуска, сразу меняет находки.
 */
export const useSearch = create<Search>()((set, get) => ({
  ...NO_FILTERS,
  text: "",
  query: null,
  scope: "server",
  sort: "newest",
  history: readHistory(),
  editing: false,
  setText: (text) => set({ text }),
  patch: (next) => set(next),
  setScope: (scope) => set({ scope }),
  setSort: (sort) => set({ sort }),
  setEditing: (editing) => set({ editing }),
  submit: (text) => {
    const query = (text ?? get().text).trim();
    const history = query ? [query, ...get().history.filter((item) => item !== query)].slice(0, HISTORY_MAX) : get().history;
    writeHistory(history);
    set({ text: query, query, history });
  },
  clear: () => set({ ...NO_FILTERS, text: "", query: null }),
  forget: () => {
    writeHistory([]);
    set({ history: [] });
  },
}));

/** Фильтры из состояния — без набранного и служебного. */
export function filtersOf(state: Filters): Filters {
  return {
    authors: state.authors,
    channels: state.channels,
    mentions: state.mentions,
    has: state.has,
    after: state.after,
    before: state.before,
    pinned: state.pinned,
    authorType: state.authorType,
    thread: state.thread,
  };
}
